import { useEffect, useRef } from "react";
import { Mic, Loader2 } from "lucide-react";
import { unlockBeep } from "@/lib/langs";

export function usePtt(rec, disabled) {
  const downAt = useRef(0);
  const onPointerDown = (e) => {
    e.preventDefault();
    unlockBeep();
    if (disabled) return;
    if (rec.isActive()) return rec.stop();
    downAt.current = Date.now();
    rec.start();
  };
  const onPointerUp = () => {
    if (rec.isActive() && downAt.current && Date.now() - downAt.current > 450) rec.stop();
    downAt.current = 0;
  };
  useEffect(() => {
    const typing = (e) => ["INPUT", "TEXTAREA"].includes(e.target.tagName) || e.target.isContentEditable;
    const kd = (e) => {
      if (e.code !== "Space" || e.repeat || typing(e) || disabled) return;
      e.preventDefault();
      if (!rec.isActive()) rec.start();
    };
    const ku = (e) => {
      if (e.code !== "Space" || typing(e)) return;
      rec.stop();
    };
    window.addEventListener("keydown", kd);
    window.addEventListener("keyup", ku);
    return () => {
      window.removeEventListener("keydown", kd);
      window.removeEventListener("keyup", ku);
    };
  }, [rec, disabled]);
  return { onPointerDown, onPointerUp, onPointerLeave: onPointerUp };
}

export const PttReactor = ({ rec, processing, handlers, label: customLabel }) => {
  const s = 1 + rec.level * 0.5;
  const label = customLabel || (processing ? "AURORA PROCESSING" : rec.recording ? "LISTENING — RELEASE TO SEND" : "HOLD TO SPEAK · TAP TO TOGGLE · SPACE");
  return (
    <div className="flex flex-col items-center py-8" data-testid="ptt-reactor">
      <div className="relative flex h-56 w-56 items-center justify-center">
        <svg viewBox="0 0 200 200" className="hud-ring-spin-slow absolute inset-0 text-foreground/15">
          <circle cx="100" cy="100" r="96" fill="none" stroke="currentColor" strokeDasharray="1 5" />
        </svg>
        <svg viewBox="0 0 200 200" className={`absolute inset-3 ${rec.recording || processing ? "hud-ring-spin" : "hud-ring-spin-slow"}`}>
          <circle cx="100" cy="100" r="92" fill="none" stroke="var(--au-cyan)" strokeOpacity="0.5" strokeDasharray="60 20 10 20" />
        </svg>
        <svg viewBox="0 0 200 200" className="hud-ring-spin-rev absolute inset-8">
          <circle cx="100" cy="100" r="92" fill="none" stroke="var(--au-violet)" strokeOpacity="0.5" strokeDasharray="4 10" strokeWidth="3" />
        </svg>
        {rec.recording && <span className="ripple absolute inset-12 rounded-full border-2 border-[color:var(--au-green)]" />}
        <button
          {...handlers}
          className="relative z-10 flex h-28 w-28 touch-none select-none items-center justify-center rounded-full border transition-[transform,background-color,box-shadow] duration-200"
          style={{
            transform: `scale(${s})`,
            background: rec.recording ? "var(--au-red)" : "hsl(var(--card))",
            borderColor: rec.recording ? "var(--au-red)" : "var(--au-green)",
            boxShadow: rec.recording ? "0 0 40px rgba(255,59,48,0.55)" : "0 0 30px color-mix(in srgb, var(--au-green) 30%, transparent)",
          }}
          data-testid="room-ptt-button" aria-label="Push to talk"
        >
          {processing ? <Loader2 size={34} className="animate-spin text-[color:var(--au-cyan)]" /> : <Mic size={34} className={rec.recording ? "text-white" : "text-[color:var(--au-green)]"} />}
        </button>
      </div>
      <span className="mono-label mt-4 text-center !text-[10px]" data-testid="ptt-status">{label}</span>
    </div>
  );
};
